import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import pick from 'lodash/pick';
import pickBy from 'lodash/pickBy';

import DatePicker from 'components/ui/date_picker';
import RelativeDateInput from './relative_date_input';
import { calculateDate, formatDate } from 'bundles/apex/dates/helpers';

const clearRelativeDate = {
  relativeDateId: null,
  static: true
};

export default function ManagedApexDateInput({
  'aria-label': ariaLabel,
  className,
  dateNameWhitelist,
  deal,
  disabled,
  fields,
  id,
  name,
  required,
  value,
  onChange = () => {},
  onValueChange = () => {},
}) {
  const dateFields = useMemo(() => {
    const dateFields = {
      ...pickBy(fields, field => field.isDate),
      ...pickBy(deal, dealValue => dealValue && dealValue.date && dealValue.adHoc)
    };

    let whitelistedFields;
    if (dateNameWhitelist) {
      whitelistedFields = {};
      for (const name of dateNameWhitelist) {
        const field = fields[name];
        if (field) {
          whitelistedFields[name] = fields[name];
        }
      }
    }

    return whitelistedFields || dateFields;
  }, [deal, fields, dateNameWhitelist]);
  const dateValues = useMemo(() => pick(deal, Object.keys(dateFields)), [deal, dateFields]);
  const selectableDateFields = useMemo(
    () => pickBy(dateFields, (_, dateFieldName) => dateFieldName !== name),
    [dateFields, name]
  );

  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerValue, setPickerValue] = useState();

  React.useEffect(() => {
    const oldValue = pickerValue || value;
    if (oldValue) {
      const date = calculateDate(deal, fields, name, oldValue);
      if (date !== oldValue.date) {
        const value = { ...oldValue, date };
        updateValue(value);
      }
    }
  }, [deal]);

  function handleOpenChange(open) {
    setPickerOpen(open);
    setPickerValue(open ? { ...value } : null);
  }

  function handleChange(changes) {
    const value = { ...pickerValue, ...changes };
    value.date = calculateDate(deal, fields, name, value);
    updateValue(value);
  }

  function updateValue(value) {
    const event = { target: { name, value } };
    onChange({ [name]: value }, event);
    onValueChange(value, event);
    setPickerValue(value);
  }

  value = pickerValue || value;

  return (
    <DatePicker
      ariaLabel={ariaLabel}
      className={className}
      disabled={disabled}
      id={id}
      label={value?.static ? null : formatDate(deal, dateFields, value)}
      open={pickerOpen}
      required={required || !value?.static}
      value={value?.date ?? ''}
      onChange={date => handleChange({ ...clearRelativeDate, date: date || null })}
      onOpenChange={handleOpenChange}
    >
      <RelativeDateInput
        dateFields={selectableDateFields}
        dateValues={dateValues}
        value={value || {}}
        onCancel={() => handleOpenChange(false)}
        onChange={handleChange}
      />
    </DatePicker>
  );
}

ManagedApexDateInput.propTypes = {
  'aria-label': PropTypes.string,
  className: PropTypes.string,
  dateNameWhitelist: PropTypes.arrayOf(PropTypes.string),
  deal: PropTypes.object.isRequired,
  disabled: PropTypes.bool,
  fields: PropTypes.objectOf(PropTypes.shape({
    isDate: PropTypes.bool,
  })).isRequired,
  id: PropTypes.string,
  name: PropTypes.string,
  required: PropTypes.bool,
  value: PropTypes.shape({
    date: PropTypes.string,
    modifier: PropTypes.number,
    operator: PropTypes.string,
    relativeDateId: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number
    ]),
    static: PropTypes.bool,
    units: PropTypes.string,
  }),
  onChange: PropTypes.func,
  onValueChange: PropTypes.func,
};
